import React, { useContext, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, Select, MenuItem, Snackbar, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import HandoutImageLoader from './handout-image-loader';
import UserApi from '../../../rest-api/user-api';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import { getCTAUpgradeImageUrl } from '../../../config';
import customiseIcon from '../../../assets/icons/CustomiseHandout.svg';
import addToBundleIcon from '../../../assets/icons/AddToBundle.svg';
import shareIcon from '../../../assets/icons/ShareEnvelop.svg';
import QuickShareDialog from './quick-share-dialog';
import { getHandoutBundleUpgradeTarget } from '../../../utils/subscription-plan-utils';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const PAGE_OPTIONS = [4, 8, 12];

const isHandoutNewByCreatedAt = (handout, now = Date.now()) => {
    if (!handout || typeof handout.createdAt !== 'number') return false;
    return handout.createdAt >= now - THIRTY_DAYS_MS;
};

const getNewHandouts = (contentData, now = Date.now()) => {
    return Object.values(contentData || {})
        .filter((item) => isHandoutNewByCreatedAt(item, now))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};

const getVisiblePages = (currentPage, pageCount) => {
    if (pageCount <= 5) return Array.from({ length: pageCount }, (_, index) => index + 1);
    if (currentPage <= 3) return [1, 2, 3, 'ellipsis', pageCount];
    if (currentPage >= pageCount - 2) return [1, 'ellipsis', pageCount - 2, pageCount - 1, pageCount];
    return [1, 'ellipsis', currentPage, 'ellipsis', pageCount];
};

const NewHandouts = observer(() => {
    const navigate = useNavigate();
    const { contentStore, imageStore, authStore, userDataStore } = useContext(StoreContext);

    const [itemsPerPage, setItemsPerPage] = useState(4);
    const [currentPage, setCurrentPage] = useState(1);
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [upgradeCTAClinicOpen, setUpgradeCTAClinicOpen] = useState(false);
    const [quickShareOpen, setQuickShareOpen] = useState(false);
    const [quickShareHandout, setQuickShareHandout] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarStatus, setSnackbarStatus] = useState('success');

    const newHandouts = useMemo(() => getNewHandouts(contentStore?.contentData), [contentStore?.contentData]);
    const pageCount = Math.max(1, Math.ceil(newHandouts.length / itemsPerPage));
    const currentSafePage = Math.min(currentPage, pageCount);

    const paginatedHandouts = useMemo(() => {
        const start = (currentSafePage - 1) * itemsPerPage;
        return newHandouts.slice(start, start + itemsPerPage);
    }, [newHandouts, currentSafePage, itemsPerPage]);

    const visiblePages = getVisiblePages(currentSafePage, pageCount);

    const handleCardClick = (handout) => {
        navigate(`/handout/${handout.contentId}`);
    };

    const handleAddToBundle = async (e, handout) => {
        e.stopPropagation();
        const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';

        const upgradeTarget = getHandoutBundleUpgradeTarget(userDataStore?.subscriptionData?.accountType);
        if (upgradeTarget === 'professional') {
            setUpgradeCTAOpen(true);
            return;
        }
        if (upgradeTarget === 'clinic') {
            setUpgradeCTAClinicOpen(true);
            return;
        }

        const userHandoutsJson = userDataStore.userData.handouts || '[]';
        const userHandouts = Array.isArray(userDataStore.userData.handouts)
            ? userDataStore.userData.handouts
            : JSON.parse(userHandoutsJson);

        const existingContentIds = userHandouts.map((h) => h.contentId);
        if (existingContentIds.includes(handout.contentId)) {
            setSnackbarMessage('Handout already exists in the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        if (userHandouts.length >= 5) {
            setSnackbarMessage('Cannot add more than 5 handouts to the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        const nextHandouts = [
            ...userHandouts,
            { contentId: handout.contentId, contentName: handout.contentName, categories: '' },
        ];
        const payload = { handouts: nextHandouts };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }

            userDataStore.updateUserHandouts(nextHandouts);
            setSnackbarMessage('Handout added to bundle');
            setSnackbarStatus('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error adding handout to bundle:', error);
            setSnackbarMessage('Failed to add handout to bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
        }
    };

    const handleQuickShare = (e, handout) => {
        e.stopPropagation();
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            setUpgradeCTAOpen(true);
            return;
        }
        setQuickShareHandout(handout);
        setQuickShareOpen(true);
    };

    const handleCustomise = (e, handout) => {
        e.stopPropagation();
        localStorage.removeItem('loadCustomisedHandout');
        localStorage.setItem('openInCustomiseMode', 'true');
        navigate(`/handout/${handout.contentId}`);
    };

    return (
        <Box
            sx={{
                background: '#F0F0FC',
                maxWidth: '1440px',
                margin: '0 auto',
                width: '100%',
                px: { xs: '22px', md: '40px' },
                pt: { xs: '22px', md: '39px' },
                pb: { xs: '16px', md: '24px' },
                boxSizing: 'border-box',
                minHeight: 'calc(100vh - 83px)',
            }}
        >
            <Typography
                sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '34px', md: '40px' },
                    lineHeight: { xs: '36px', md: '40px' },
                    letterSpacing: { xs: '-1px', md: '-1.6px' },
                    color: '#000453',
                    mb: '35px',
                }}
            >
                New handouts
            </Typography>

            <Box
                sx={{
                    background: '#FFFFFF',
                    borderRadius: { xs: '24px', md: '30px' },
                    p: { xs: '14px', md: '21px' },
                    minHeight: { xs: '350px', md: '420px' },
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {paginatedHandouts.length === 0 ? (
                    <Box
                        sx={{
                            minHeight: { xs: '220px', md: '280px' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            px: '16px',
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: { xs: '24px', md: '32px' },
                                lineHeight: { xs: '28px', md: '34px' },
                                letterSpacing: { xs: '-0.6px', md: '-1.2px' },
                                color: '#000453',
                            }}
                        >
                            No new handouts found
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                lg: `repeat(${Math.min(itemsPerPage, 4)}, minmax(0, 1fr))`,
                            },
                            gap: { xs: '14px', md: '21px' },
                        }}
                    >
                        {paginatedHandouts.map((handout) => (
                            <NewHandoutCard
                                key={handout.contentId}
                                handout={handout}
                                imageStore={imageStore}
                                onClick={() => handleCardClick(handout)}
                                onAddToBundle={(e) => handleAddToBundle(e, handout)}
                                onQuickShare={(e) => handleQuickShare(e, handout)}
                                onCustomise={(e) => handleCustomise(e, handout)}
                            />
                        ))}
                    </Box>
                )}

                <Box
                    sx={{
                        mt: 'auto',
                        pt: { xs: '16px', md: '14px' },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '10px',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6.5px' }}>
                        <PaginationButton
                            disabled={currentSafePage <= 1}
                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        >
                            <ChevronLeftIcon sx={{ color: '#616063', fontSize: '20px' }} />
                        </PaginationButton>

                        {visiblePages.map((page, index) => {
                            if (page === 'ellipsis') {
                                return (
                                    <Box
                                        key={`ellipsis-${index}`}
                                        sx={{
                                            width: '42px',
                                            height: '42px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#616063',
                                            fontSize: '21px',
                                        }}
                                    >
                                        ...
                                    </Box>
                                );
                            }

                            return (
                                <PaginationButton
                                    key={page}
                                    active={page === currentSafePage}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </PaginationButton>
                            );
                        })}

                        <PaginationButton
                            disabled={currentSafePage >= pageCount}
                            onClick={() => setCurrentPage((prev) => Math.min(pageCount, prev + 1))}
                        >
                            <ChevronRightIcon sx={{ color: '#616063', fontSize: '20px' }} />
                        </PaginationButton>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '16px',
                                color: '#616063',
                            }}
                        >
                            Items per page
                        </Typography>
                        <Select
                            value={itemsPerPage}
                            size="small"
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            sx={{
                                height: '30px',
                                minWidth: '63px',
                                borderRadius: '10px',
                                color: '#616063',
                                '& .MuiSelect-select': {
                                    py: '2px',
                                    fontSize: '16px',
                                    fontFamily: 'Inter, sans-serif',
                                },
                            }}
                        >
                            {PAGE_OPTIONS.map((count) => (
                                <MenuItem key={count} value={count}>
                                    {count}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>
            </Box>

            <CTAUpgradeDialog
                open={upgradeCTAClinicOpen}
                handleClose={() => setUpgradeCTAClinicOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Upgrade now for clinic-wide access and advanced features!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to add more users and enjoy full access to our features!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                    'Bundling of handouts and diagrams for seamless sharing',
                    'Personalise with your clinic logo',
                ]}
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => setUpgradeCTAOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock unlimited sharing and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts',
                    'Unlimited digital sharing of all anatomy diagrams',
                    'Personalise with your clinic details',
                ]}
                imageUrl={getCTAUpgradeImageUrl('professional')}
            />

            {quickShareHandout && (
                <QuickShareDialog
                    open={quickShareOpen}
                    handout={quickShareHandout}
                    onClose={() => {
                        setQuickShareOpen(false);
                        setQuickShareHandout(null);
                    }}
                />
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={
                    <Box display="flex" alignItems="center">
                        {snackbarMessage}
                        {snackbarStatus === 'success' ? (
                            <CheckIcon sx={{ ml: 1, color: 'white' }} />
                        ) : (
                            <CloseIcon sx={{ ml: 1, color: 'red' }} />
                        )}
                    </Box>
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundImage: 'linear-gradient(132deg, #FA96C3 -2.07%, #FF669B 15.82%, #BD8DE8 116.64%)',
                        borderRadius: 2,
                    },
                }}
            />
        </Box>
    );
});

const PaginationButton = ({ children, active = false, disabled = false, onClick }) => (
    <Box
        onClick={disabled ? undefined : onClick}
        sx={{
            width: '42px',
            height: '42px',
            borderRadius: '32px',
            border: active ? 'none' : '1.313px solid #E5E5E5',
            background: active ? '#FF669B' : '#FFFFFF',
            color: active ? '#FFFFFF' : '#616063',
            fontFamily: 'Inter, sans-serif',
            fontWeight: active ? 400 : 500,
            fontSize: '21px',
            lineHeight: '21px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.5 : 1,
        }}
    >
        {children}
    </Box>
);

const NewHandoutCard = ({ handout, imageStore, onClick, onAddToBundle, onQuickShare, onCustomise }) => {
    const locale = 'en-UK';
    const localeData = handout.contentData?.[locale] || [];

    const is3DModel =
        localeData.length > 0 &&
        localeData.every((section) =>
            section.blocks.every((block) => block.type === '3dModel' || block.type === 'documentTitle'),
        );

    const modelBlock = is3DModel
        ? localeData.flatMap((section) => section.blocks).find((block) => block.type === '3dModel')
        : null;
    const illustrationBlock = is3DModel
        ? null
        : localeData.flatMap((section) => section.blocks).find((block) => block.type === 'illustration');

    const imageSrc = modelBlock?.imageSrc || illustrationBlock?.src || null;

    return (
        <Box
            onClick={onClick}
            sx={{
                background: '#F0F0FC',
                borderRadius: '20px',
                p: { xs: '14px', md: '21px' },
                minHeight: { xs: '411px', md: '414px' },
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
            }}
        >
            <Box
                sx={{
                    background: '#FFFFFF',
                    borderRadius: '10px',
                    minHeight: { xs: '254px', md: '256px' },
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <HandoutImageLoader
                    src={imageSrc}
                    alt={handout.contentName}
                    imageStore={imageStore}
                    handoutData={handout}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                />
            </Box>

            <Typography
                sx={{
                    mt: '10px',
                    minHeight: '56px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '22px',
                    lineHeight: '26px',
                    letterSpacing: '-0.44px',
                    color: '#000453',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {handout.contentName}
            </Typography>

            <Box sx={{ mt: 'auto' }}>
                <Box sx={{ height: '2px', background: 'rgba(0, 4, 83, 0.10)', mb: '8px' }} />
                <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <ActionButton
                        icon={customiseIcon}
                        alt="Customise"
                        tooltip="Open in customise mode"
                        onClick={onCustomise}
                    />
                    <Box sx={{ display: 'flex', gap: '10px' }}>
                        <ActionButton
                            icon={addToBundleIcon}
                            alt="Add to bundle"
                            tooltip="Add to bundle"
                            onClick={onAddToBundle}
                        />
                        <ActionButton icon={shareIcon} alt="Share" tooltip="Quick share" onClick={onQuickShare} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const ActionButton = ({ icon, alt, tooltip, onClick }) => (
    <Tooltip title={tooltip} arrow>
        <Box
            onClick={onClick}
            sx={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: '#FF669B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { background: '#FF85B0' },
            }}
        >
            <img src={icon} alt={alt} width={22} height={22} />
        </Box>
    </Tooltip>
);

export { isHandoutNewByCreatedAt, getNewHandouts, THIRTY_DAYS_MS };
export default NewHandouts;
